import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Upload, FileText, Loader2, ShoppingCart, Sparkles
} from 'lucide-react';

export function DocumentUploadTab({ 
  selectedFile, documentType, setDocumentType,
  uploading, processing, onFileSelect, onUploadAndProcess, getFileIcon 
  ,customers = [], customerId, setCustomerId
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          Upload Document for AI Processing
        </CardTitle>
        <CardDescription>
          Upload a customer inquiry or purchase order. AI will extract all line items automatically.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <Label>Document Type *</Label>
            <Select value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger data-testid="document-type-select">
                <SelectValue placeholder="Select document type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inquiry">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-500" />
                    Customer Inquiry (Parts)
                  </div>
                </SelectItem>
                <SelectItem value="purchase_order">
                  <div className="flex items-center gap-2">
                    <ShoppingCart className="h-4 w-4 text-green-500" />
                    Purchase Order (Sales Order)
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {documentType === 'inquiry' 
                ? 'Extracts parts data and creates draft parts for review'
                : documentType === 'purchase_order'
                ? 'Extracts PO line items and creates draft sales order'
                : 'Select the type of document you are uploading'}
            </p>
          </div>

          {documentType === 'inquiry' && (
            <div className="space-y-2">
              <Label>Customer</Label>
              <Select value={customerId || 'unassigned'} onValueChange={(value) => setCustomerId(value === 'unassigned' ? '' : value)}>
                <SelectTrigger data-testid="document-customer-select">
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Assign later</SelectItem>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>{customer.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">The upload creates a traceable Inquiry record.</p>
            </div>
          )}

          <div className="space-y-2">
            <Label>Upload File *</Label>
            <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls"
                onChange={onFileSelect}
                className="hidden"
                id="file-upload"
                data-testid="file-upload-input"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                {selectedFile ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-center gap-2">
                      {getFileIcon(selectedFile.type)}
                      <span className="font-medium">{selectedFile.name}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                    <p className="text-xs text-primary">Click to change file</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="h-10 w-10 mx-auto text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Click to upload or drag and drop
                    </p>
                    <p className="text-xs text-muted-foreground">
                      PDF, JPEG, PNG, WEBP, Excel (max 10MB)
                    </p>
                  </div>
                )}
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={onUploadAndProcess}
            disabled={!selectedFile || !documentType || uploading || processing}
            data-testid="upload-process-btn"
            className="min-w-[200px]"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : processing ? (
              <>
                <Sparkles className="h-4 w-4 mr-2 animate-pulse" />
                AI Processing...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Upload & Process with AI
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
